import { Capacitor } from '@capacitor/core';
import { defineCustomElement as definirJeepSqlite } from 'jeep-sqlite/dist/components/jeep-sqlite.js';

import { initDatabase } from './db/index.js';
import { ApiClient } from './net/ApiClient.js';
import { AuthClient, baseHttpDeWs } from './net/AuthClient.js';
import { criarFonteDeDados } from './net/fonteDeDados.js';
import { TabletNetworkManager } from './net/NetworkManager.js';
import { ligarInscricaoDeSalas } from './net/salas.js';
import { SCENES } from './game/config/constants.js';
import { ligarEstacaoDoGrupo } from './game/services/estacaoDoGrupo.js';
import { Sessao, SessaoEstacao } from './game/services/Sessao.js';
import { aplicarTextosGuardados, carregarTextos } from './game/services/textos.js';
import StartGame from './game/main.js';
import { identificarTablet } from './net/identificacaoDoTablet.js';

definirJeepSqlite();

/**
 * `__APP_MODE__` vem do vite.config.js (flag `VITE_APP_MODE`):
 * - `dev`: conteúdo mockado, sem login no servidor e sem WebSocket.
 * - `prd`: sessão e conteúdo pela API do backend.
 * Ver `src/config/appMode.js` e o plano `tablet-frontend` (`DP-TF-003`).
 */
const EM_PRD = __APP_MODE__ === 'prd';
/** `__APP_PAPEL__` (flag `VITE_APP_PAPEL`): o build do facilitador é outro aplicativo. */
const EH_FACILITADOR = __APP_PAPEL__ === 'facilitador';
// Cada papel tem o seu vínculo salvo; o token das chamadas é o do papel do build.
const sessaoDoPapel = EH_FACILITADOR ? SessaoEstacao : Sessao;

// Id do build: vale no navegador e enquanto o ANDROID_ID não chega (ver `iniciar`).
const tabletIdDoBuild = import.meta.env.VITE_TABLET_ID ?? 'tablet_01';

// Same-origin por padrao: sem `VITE_WS_URL`, deriva ws/wss da propria pagina.
// Funciona igual em localhost e atras de tunel (ngrok), sem CORS e sem rebuild
// por URL. `VITE_WS_URL` continua valendo quando definido no build.
function mesmaOrigemComoWs() {
    const { protocol, host } = window.location;
    return `${protocol === 'https:' ? 'wss:' : 'ws:'}//${host}/`;
}

// No aplicativo Android (Capacitor) a "propria origem" e o WebView (`https://localhost`), nao o
// servidor: sem `VITE_WS_URL` no build nativo o login e o WebSocket apontariam para o proprio
// aparelho. Ali o endereco do servidor e obrigatorio; sem ele, cai no padrao antigo de dev.
function enderecoDoServidor() {
    if (import.meta.env.VITE_WS_URL) return import.meta.env.VITE_WS_URL;
    if (Capacitor.isNativePlatform()) {
        console.error('[App] Build Android sem VITE_WS_URL: defina o endereco do servidor no build.');
        return 'ws://localhost:8000/';
    }
    return mesmaOrigemComoWs();
}

const serverUrl = enderecoDoServidor();

const apiUrl = import.meta.env.VITE_API_URL || baseHttpDeWs(serverUrl);
const auth = new AuthClient(apiUrl);
// Sessão morta no servidor (HTTP 401 ou WebSocket recusado): quem trata é o jogo, depois de montado.
let aoSessaoInvalida = () => {};
const api = new ApiClient(apiUrl, {
    obterToken: () => sessaoDoPapel.token,
    aoNaoAutorizado: () => aoSessaoInvalida('http_401'),
});
const redeTablet = new TabletNetworkManager(serverUrl, tabletIdDoBuild, {
    obterToken: () => sessaoDoPapel.token,
});

// Sala da turma (abertura de fase, resultados, `boss_final_iniciada`) e sala
// do grupo, a cada `pronto` — sem a da turma o tablet nunca sai do Aguarde.
ligarInscricaoDeSalas(redeTablet);

/** O banco local abre em paralelo: uma falha nele não pode segurar a tela de login. */
async function abrirBanco() {
    try {
        await initDatabase();
        console.log('[DB] Banco local pronto.');
    } catch (erro) {
        console.error('[DB] Falha ao abrir o banco local:', erro);
    }
}

async function iniciar() {
    // No Android o tablet se identifica pelo ANDROID_ID. A rede ainda não conectou (isso só acontece
    // depois do login), então trocar o id aqui vale para o WebSocket e para o `dispositivo` do login.
    const tabletId = await identificarTablet(tabletIdDoBuild);
    redeTablet.tabletId = tabletId;

    // Textos das telas alterados pelo admin (`VAR3`): primeiro o que ficou guardado, depois o
    // servidor. Em `dev` vale só o padrão do aplicativo; em `prd` sem rede, os guardados ou o padrão.
    if (EM_PRD) {
        aplicarTextosGuardados(localStorage);
        await carregarTextos({ baseUrl: apiUrl, armazenamento: localStorage, tempoLimiteMs: 2500 });
    }

    if (EH_FACILITADOR) {
        // Import dinâmico atrás de um literal de build: no aplicativo do
        // participante este ramo é código morto e nada do facilitador é empacotado.
        const { iniciarFacilitador } = await import('./facilitador.js');
        window.game = await iniciarFacilitador({ auth, api, rede: redeTablet, emPrd: EM_PRD });
        // O Boss Final roda no painel administrativo (adm-frontend), não em tablet.
        return;
    }

    const fonte = await criarFonteDeDados({
        auth,
        api,
        rede: redeTablet,
        dispositivo: tabletId,
        codigo: import.meta.env.VITE_CODIGO ?? null,
    });
    console.info(`[App] Modo ${fonte.modo}.`);
    if (Sessao.ativa) fonte.definirAutor(Sessao.atual.participanteId, Sessao.atual);

    // O jogo vive em src/game/ (Boot → Preloader → Login → Aguarde → fases).
    const game = StartGame('game', { fonte });
    game.network = redeTablet;
    // Handle de debug/automação (testes de layout em headless browser).
    window.game = game;

    game.events.on('sessao:iniciada', (sessao) => fonte.definirAutor(sessao.participanteId, sessao));

    ligarEstacaoDoGrupo({
        fonte,
        cenas: game.scene,
        participanteId: () => Sessao.atual?.participanteId ?? null,
        aviso: criarAvisoDePausa(),
    });

    // O facilitador ligou ou desligou Insights, Materiais ou Ranking: o Aguarde é redesenhado.
    fonte.aoVisibilidade(() => {
        if (game.scene.isActive(SCENES.AGUARDE)) game.scene.getScene(SCENES.AGUARDE).scene.restart({});
    });

    if (!EM_PRD) return;

    // A cada conexão pronta o estado do grupo é relido: o comando do facilitador
    // pode ter passado com o tablet fora do ar.
    redeTablet.on('pronto', () => fonte.sincronizarEstacoes());

    // Em prd o WebSocket só abre com sessão: no login ou no vínculo já salvo.
    game.events.on('sessao:iniciada', () => redeTablet.connect());
    game.events.on('sessao:encerrada', () => redeTablet.desconectar());
    if (Sessao.ativa) redeTablet.connect();

    aoSessaoInvalida = (motivo) => {
        if (!Sessao.ativa) return;
        console.error(`[Auth] Sessão inválida (${motivo}). É preciso vincular de novo.`);
        Sessao.encerrar();
        // Sem isto a rede fica no estado "sessão inválida" e recusa conectar no próximo login.
        redeTablet.desconectar();
        game.scene.getScenes(true).forEach((cena) => cena.scene.stop());
        game.scene.start('Login');
    };
    redeTablet.on('sessao_invalida', ({ motivo }) => aoSessaoInvalida(motivo));
}

/** Faixa sobre o jogo enquanto o facilitador mantém o grupo pausado. */
function criarAvisoDePausa() {
    const elemento = document.createElement('div');
    elemento.id = 'aviso-pausa';
    elemento.hidden = true;
    elemento.setAttribute('role', 'status');
    const PADRAO = 'ATIVIDADE PAUSADA PELO FACILITADOR';
    elemento.textContent = PADRAO;
    document.body.appendChild(elemento);
    return {
        mostrar: (texto = PADRAO) => { elemento.textContent = texto; elemento.hidden = false; },
        esconder: () => { elemento.hidden = true; },
    };
}

abrirBanco();
iniciar();
